import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import CampaignPreview from "@/components/CampaignPreview";
import {
  Save,
  Rocket,
  Eye,
  Mail,
  MessageSquare,
  Globe,
  ChevronLeft,
  ShieldCheck,
  Sparkles,
  Plus,
  Trash2,
} from "lucide-react";

const CHANNELS = [
  { id: "email", label: "Email", icon: Mail },
  { id: "sms", label: "SMS", icon: MessageSquare },
  { id: "landing_page", label: "Landing page", icon: Globe },
];

const CATEGORY_OPTIONS = [
  "sleep",
  "metabolic",
  "wellness",
  "longevity",
  "diagnostics",
  "reactivation",
  "subscription",
  "upsell",
  "acquisition",
];

const EMPTY = {
  name: "",
  goal: "",
  category: "wellness",
  audience_id: null,
  channels: [],
  schedule_at: null,
  landing_page: null,
  notes: "",
};

export default function PortalCampaignEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [audiences, setAudiences] = useState([]);
  const [tier, setTier] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    api.get("/clinic/campaigns/audiences")
      .then((r) => setAudiences(r.data?.audiences || []))
      .catch(() => {});
    api.get("/clinic/campaigns/tier")
      .then((r) => setTier(r.data))
      .catch(() => {});
    if (!isNew) {
      api.get(`/clinic/campaigns/${id}`)
        .then((r) => setForm({ ...EMPTY, ...r.data }))
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const isPremium = tier?.tier === "premium" || tier?.tier === "enterprise";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setChannel = (channel, patch) =>
    setForm((f) => ({
      ...f,
      channels: f.channels.map((c) =>
        c.channel === channel ? { ...c, ...patch } : c,
      ),
    }));
  const toggleChannel = (channel) =>
    setForm((f) => {
      const exists = f.channels.find((c) => c.channel === channel);
      if (exists) {
        return { ...f, channels: f.channels.filter((c) => c.channel !== channel) };
      }
      return {
        ...f,
        channels: [
          ...f.channels,
          {
            channel,
            enabled: true,
            delay_minutes: f.channels.length * 1440,
            subject: channel === "email" ? "" : null,
            body_text: channel === "sms" ? "" : null,
            sender_name: "Your Clinic",
            blocks: [],
          },
        ],
      };
    });

  const save = async ({ launch = false } = {}) => {
    if (!form.name.trim() || !form.goal.trim()) {
      toast.error("Campaign name and goal are required.");
      return;
    }
    setSaving(true);
    try {
      let cid = isNew ? null : id;
      if (isNew) {
        const r = await api.post("/clinic/campaigns", form);
        cid = r.data.id;
        toast.success("Campaign saved.");
      } else {
        await api.patch(`/clinic/campaigns/${id}`, form);
        toast.success("Saved.");
      }
      if (launch) {
        await api.post(`/clinic/campaigns/${cid}/launch`);
        toast.success("Campaign launched.");
        navigate("/portal/marketing/campaigns?tab=campaigns");
        return;
      }
      if (isNew) {
        navigate(`/portal/marketing/campaigns/${cid}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ToolPageShell eyebrow="Marketing · Campaign" title="Loading campaign…">
        <div className="text-sm text-slate-500">…</div>
      </ToolPageShell>
    );
  }

  return (
    <ToolPageShell
      eyebrow="Marketing · Campaign"
      title={isNew ? "New campaign" : form.name || "Untitled campaign"}
      intro="Configure messaging, channels, audience, and schedule. Save as draft or launch when ready."
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/portal/marketing/campaigns?tab=campaigns")}
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-slate-100 hover:bg-slate-200"
          >
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            data-testid="campaign-editor-preview-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          <button
            type="button"
            onClick={() => save({ launch: false })}
            disabled={saving}
            data-testid="campaign-save-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            onClick={() => save({ launch: true })}
            disabled={saving}
            data-testid="campaign-launch-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
          >
            <Rocket className="w-3.5 h-3.5" /> Save & launch
          </button>
        </div>
      }
    >
      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-xs text-amber-900 mb-5 flex items-start gap-2" data-testid="campaign-editor-compliance">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-amber-700" />
        <span>
          <strong>Educational outreach only.</strong> Avoid medical claims and
          guaranteed outcomes. SMS messages must include opt-out language.
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Basics + audience */}
        <div className="lg:col-span-1 space-y-4">
          <Section title="Basics">
            <Field label="Campaign name">
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="campaign-field-name"
              />
            </Field>
            <Field label="Goal">
              <textarea
                rows={2}
                value={form.goal}
                onChange={(e) => set("goal", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="campaign-field-goal"
              />
            </Field>
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize"
                data-testid="campaign-field-category"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Audience">
              <select
                value={form.audience_id || ""}
                onChange={(e) => set("audience_id", e.target.value || null)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="campaign-field-audience"
              >
                <option value="">No audience filter (all consumers)</option>
                {audiences.map((a) => (
                  <option key={a.id} value={a.id}>
                    {`${a.name} (${a.member_count})`}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Schedule (ISO datetime, optional)">
              <input
                type="datetime-local"
                value={form.schedule_at ? form.schedule_at.slice(0, 16) : ""}
                onChange={(e) =>
                  set("schedule_at", e.target.value ? new Date(e.target.value).toISOString() : null)
                }
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                data-testid="campaign-field-schedule"
              />
            </Field>
          </Section>

          {isPremium && (
            <Section title="AI Campaign Assistant" subtitle="Engagement copy only — no medical claims.">
              <button
                type="button"
                onClick={() => setAiOpen(true)}
                data-testid="campaign-ai-open"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold bg-violet-50 text-violet-700 hover:bg-violet-100"
              >
                <Sparkles className="w-4 h-4" /> Open AI assistant
              </button>
            </Section>
          )}
        </div>

        {/* Channels */}
        <div className="lg:col-span-2 space-y-4">
          <Section
            title="Channels"
            subtitle="Add the surfaces this campaign will deploy through. Multi-channel sequencing requires Growth or higher."
          >
            <div className="flex flex-wrap gap-2 mb-3">
              {CHANNELS.map((c) => {
                const active = form.channels.find((x) => x.channel === c.id);
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChannel(c.id)}
                    data-testid={`campaign-channel-toggle-${c.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      active
                        ? "bg-slate-900 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {c.label}
                    {active ? " ✓" : " +"}
                  </button>
                );
              })}
            </div>
            {form.channels.length === 0 && (
              <div className="text-sm text-slate-500">Pick at least one channel above.</div>
            )}
            <div className="space-y-3">
              {form.channels.map((ch) => (
                <ChannelEditor
                  key={ch.channel}
                  channel={ch}
                  onChange={(patch) => setChannel(ch.channel, patch)}
                />
              ))}
            </div>
          </Section>
        </div>
      </div>

      {previewOpen && (
        <CampaignPreview campaign={form} onClose={() => setPreviewOpen(false)} />
      )}

      {aiOpen && isPremium && (
        <AIDialog
          category={form.category}
          audienceId={form.audience_id}
          audiences={audiences}
          onClose={() => setAiOpen(false)}
          onApply={({ intent, text }) => {
            if (intent === "subject_line") {
              const firstLine = text.split("\n").find((l) => l.trim()) || text;
              setChannel("email", { subject: firstLine.replace(/^["']|["']$/g, "").trim() });
              toast.success("Subject line applied.");
            } else if (intent === "email_body") {
              const ch = form.channels.find((c) => c.channel === "email");
              if (ch) {
                setChannel("email", {
                  blocks: [
                    ...(ch.blocks || []),
                    { id: Math.random().toString(36).slice(2, 10), type: "paragraph", content: { text } },
                  ],
                });
                toast.success("Email body added.");
              } else {
                toast.message("Add the email channel first.");
              }
            } else if (intent === "sms") {
              setChannel("sms", { body_text: text });
              toast.success("SMS body applied.");
            }
          }}
        />
      )}
    </ToolPageShell>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
      <div>
        <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ChannelEditor({ channel, onChange }) {
  if (channel.channel === "email") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="channel-editor-email">
        <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Email</div>
        <input
          type="text"
          value={channel.subject || ""}
          onChange={(e) => onChange({ subject: e.target.value })}
          placeholder="Subject line"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
        <input
          type="text"
          value={channel.sender_name || ""}
          onChange={(e) => onChange({ sender_name: e.target.value })}
          placeholder="Sender name"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
        <BlockList
          blocks={channel.blocks || []}
          onChange={(blocks) => onChange({ blocks })}
        />
      </div>
    );
  }
  if (channel.channel === "sms") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="channel-editor-sms">
        <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">SMS</div>
        <textarea
          rows={3}
          value={channel.body_text || ""}
          onChange={(e) => onChange({ body_text: e.target.value })}
          maxLength={160}
          placeholder="SMS body — 160 characters max. Include 'Reply STOP to opt out.'"
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
        />
        <div className="text-[11px] text-slate-500">
          {(channel.body_text || "").length}/160 chars · must include opt-out language
        </div>
      </div>
    );
  }
  // Landing page
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="channel-editor-landing">
      <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Landing page</div>
      <BlockList
        blocks={channel.blocks || []}
        onChange={(blocks) => onChange({ blocks })}
      />
    </div>
  );
}

const BLOCK_TYPES = [
  { id: "headline", label: "Headline" },
  { id: "paragraph", label: "Paragraph" },
  { id: "cta_button", label: "CTA button" },
  { id: "education_section", label: "Education section" },
  { id: "testimonial", label: "Testimonial" },
  { id: "faq", label: "FAQ" },
  { id: "scheduling_link", label: "Scheduling link" },
];

function BlockList({ blocks, onChange }) {
  const add = (type) => {
    onChange([...blocks, { id: Math.random().toString(36).slice(2, 10), type, content: {} }]);
  };
  const update = (id, content) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, content: { ...b.content, ...content } } : b)));
  };
  const remove = (id) => onChange(blocks.filter((b) => b.id !== id));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {BLOCK_TYPES.map((bt) => (
          <button
            key={bt.id}
            type="button"
            onClick={() => add(bt.id)}
            className="inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-white border border-slate-200 hover:bg-slate-100"
          >
            <Plus className="w-2.5 h-2.5" /> {bt.label}
          </button>
        ))}
      </div>
      {blocks.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic">No blocks yet — add one above.</div>
      ) : (
        <div className="space-y-2">
          {blocks.map((b) => (
            <BlockRow
              key={b.id}
              block={b}
              onUpdate={(content) => update(b.id, content)}
              onRemove={() => remove(b.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Individual block editor — handles each block type with the appropriate
// input shape. Paragraphs/education use a textarea; FAQs expose Q/A pairs;
// testimonials use a multi-line input. CTAs edit `label`. Everything else
// falls back to a single text field.
function BlockRow({ block, onUpdate, onRemove }) {
  const c = block.content || {};

  const renderEditor = () => {
    if (block.type === "paragraph" || block.type === "education_section") {
      return (
        <textarea
          rows={Math.min(8, Math.max(2, (c.text || "").split("\n").length))}
          value={c.text || ""}
          onChange={(e) => onUpdate({ text: e.target.value })}
          placeholder="Write your paragraph…"
          className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs leading-relaxed"
        />
      );
    }
    if (block.type === "testimonial") {
      return (
        <textarea
          rows={3}
          value={c.text || ""}
          onChange={(e) => onUpdate({ text: e.target.value })}
          placeholder={"\"Quote here.\"\n\n— Attribution"}
          className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs italic leading-relaxed"
        />
      );
    }
    if (block.type === "cta_button") {
      return (
        <div className="flex-1 grid grid-cols-2 gap-1">
          <input
            type="text"
            value={c.label || ""}
            onChange={(e) => onUpdate({ label: e.target.value })}
            placeholder="Button label"
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
          />
          <input
            type="text"
            value={c.href || ""}
            onChange={(e) => onUpdate({ href: e.target.value })}
            placeholder="https://… or #anchor"
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs"
          />
        </div>
      );
    }
    if (block.type === "faq") {
      const items = Array.isArray(c.items) ? c.items : [];
      const setItems = (next) => onUpdate({ items: next });
      return (
        <div className="flex-1 space-y-2">
          {items.length === 0 && (
            <div className="text-[11px] text-slate-500 italic">No FAQ items yet.</div>
          )}
          {items.map((it, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-1 bg-slate-50">
              <div className="flex items-start gap-1">
                <input
                  type="text"
                  value={it.q || ""}
                  onChange={(e) => {
                    const next = items.slice();
                    next[i] = { ...next[i], q: e.target.value };
                    setItems(next);
                  }}
                  placeholder="Question"
                  className="flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold"
                />
                <button
                  type="button"
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                  className="p-1 rounded-full hover:bg-rose-50 text-rose-500"
                  aria-label="Remove FAQ"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <textarea
                rows={2}
                value={it.a || ""}
                onChange={(e) => {
                  const next = items.slice();
                  next[i] = { ...next[i], a: e.target.value };
                  setItems(next);
                }}
                placeholder="Answer"
                className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => setItems([...items, { q: "", a: "" }])}
            className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-white border border-slate-200 hover:bg-slate-100"
          >
            <Plus className="w-2.5 h-2.5" /> Add FAQ item
          </button>
        </div>
      );
    }
    if (block.type === "scheduling_link") {
      return (
        <input
          type="text"
          value={c.text || ""}
          onChange={(e) => onUpdate({ text: e.target.value })}
          placeholder="Schedule prompt"
          className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs"
        />
      );
    }
    // Default: headline & misc text-only blocks.
    return (
      <input
        type="text"
        value={c.text || ""}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder="Block content"
        className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs"
      />
    );
  };

  return (
    <div
      className="rounded-lg bg-white border border-slate-200 p-2 flex items-start gap-2"
      data-testid={`campaign-block-${block.type}`}
    >
      <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-slate-500 min-w-[90px] pt-1.5">
        {block.type.replace("_", " ")}
      </span>
      {renderEditor()}
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded-full hover:bg-rose-50 text-rose-500"
        aria-label="Remove block"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function AIDialog({ category, audienceId, audiences, onClose, onApply }) {
  const [intent, setIntent] = useState("subject_line");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const aud = audiences.find((a) => a.id === audienceId);
  const generate = async () => {
    setBusy(true);
    setOutput("");
    try {
      const r = await api.post("/clinic/campaigns/ai/assist", {
        intent,
        category,
        audience_description: aud?.description || aud?.name || "",
      });
      setOutput(r.data?.text || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-[1px] p-4" data-testid="campaign-ai-dialog">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900 inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-violet-600" /> AI Campaign Assistant
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-500">
            Engagement-focused copy only. The assistant won't generate medical advice or treatment claims.
          </div>
          <select
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            data-testid="campaign-ai-intent"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="subject_line">Email subject lines</option>
            <option value="email_body">Email body</option>
            <option value="sms">SMS body</option>
            <option value="campaign_idea">Campaign idea suggestions</option>
            <option value="segment_suggestion">Audience segment ideas</option>
          </select>
          <button
            type="button"
            onClick={generate}
            disabled={busy}
            data-testid="campaign-ai-generate"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "Generating…" : "Generate"}
          </button>
          {output && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm whitespace-pre-wrap text-slate-800" data-testid="campaign-ai-output">
              {output}
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-end gap-2">
          {output && ["subject_line", "email_body", "sms"].includes(intent) && (
            <button
              type="button"
              onClick={() => onApply({ intent, text: output })}
              data-testid="campaign-ai-apply"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500"
            >
              Apply to campaign
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
